import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Megaphone, Plus, Trash2, Eye, EyeOff, ChevronUp, ChevronDown,
  Calendar, Link as LinkIcon, Type, Image as ImageIcon, Timer, Mail, Save,
} from "lucide-react";
import { ImageUploader } from "./ImageUploader";

/* ------------------------------------------------------------------ */
/* Shared helpers                                                      */
/* ------------------------------------------------------------------ */

// HTML5 datetime-local <input> expects "YYYY-MM-DDTHH:MM" and the value is
// in the user's LOCAL timezone. We store ISO 8601 (UTC) on the backend.
function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
function localInputToIso(local) {
  if (!local) return null;
  try { return new Date(local).toISOString(); } catch { return null; }
}

/* ------------------------------------------------------------------ */
/* Banner editor                                                       */
/* ------------------------------------------------------------------ */

function BannerRow({ row, onSave, onDelete, onMove, isFirst }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => { setDraft(row); }, [row]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(row);

  return (
    <div className="bg-white border-2 border-[#f4e4c6] rounded-3xl p-4 space-y-3" data-testid={`marketing-banner-row-${draft.id}`}>
      {!draft.enabled && (
        <div className="bg-[#fff4d6] border-2 border-[#f0a988] rounded-2xl px-3 py-2 text-xs text-[#a36b29] flex items-center gap-2" data-testid={`marketing-banner-off-warning-${draft.id}`}>
          <EyeOff className="w-4 h-4 shrink-0" />
          <span><b>Currently Off</b> — visitors won't see this. Click the Off pill below to flip it Live.</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onSave({ ...draft, enabled: !draft.enabled })}
            className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full"
            style={{ background: draft.enabled ? "#dcf2e1" : "#f1f5f8", color: draft.enabled ? "#236f6b" : "#6b7280" }}
            data-testid={`marketing-banner-toggle-${draft.id}`}
          >
            {draft.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {draft.enabled ? "Live" : "Off"}
          </button>
          <div className="flex flex-col">
            <button type="button" onClick={() => onMove(draft.id, -1)} className="text-[#5a6b76] hover:text-[#236f6b]"><ChevronUp className="w-3 h-3" /></button>
            <button type="button" onClick={() => onMove(draft.id, 1)} className="text-[#5a6b76] hover:text-[#236f6b]"><ChevronDown className="w-3 h-3" /></button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => onSave(draft)} disabled={!dirty} className="btn-primary inline-flex items-center gap-1 text-xs disabled:opacity-40" data-testid={`marketing-banner-save-${draft.id}`}>
            <Save className="w-3.5 h-3.5" /> Save
          </button>
          <button type="button" onClick={() => onDelete(draft.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-full" data-testid={`marketing-banner-delete-${draft.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <label className="block">
        <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1"><Type className="w-3 h-3" /> Message</span>
        <input value={draft.message || ""} onChange={(e) => set("message", e.target.value)} maxLength={240} className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" data-testid={`marketing-banner-message-${draft.id}`} />
      </label>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76]">Link label</span>
          <input value={draft.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} maxLength={40} placeholder='e.g. "Buy the book"' className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Link URL</span>
          <input value={draft.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} placeholder="/shop or https://…" className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1"><Calendar className="w-3 h-3" /> Show from</span>
          <input type="datetime-local" value={isoToLocalInput(draft.starts_at)} onChange={(e) => set("starts_at", localInputToIso(e.target.value))} className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1"><Calendar className="w-3 h-3" /> Show until</span>
          <input type="datetime-local" value={isoToLocalInput(draft.ends_at)} onChange={(e) => set("ends_at", localInputToIso(e.target.value))} className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76]">Background color {!isFirst && <span className="text-[#a36b29] normal-case font-normal">(only the top banner's color is used for the strip)</span>}</span>
          <input type="color" value={draft.bg_color || "#236f6b"} onChange={(e) => set("bg_color", e.target.value)} disabled={!isFirst} className="mt-1 w-full h-10 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] disabled:opacity-40" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76]">Text color</span>
          <input type="color" value={draft.text_color || "#ffffff"} onChange={(e) => set("text_color", e.target.value)} disabled={!isFirst} className="mt-1 w-full h-10 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] disabled:opacity-40" />
        </label>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Popup editor                                                        */
/* ------------------------------------------------------------------ */

function PopupRow({ row, onSave, onDelete, onMove }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => { setDraft(row); }, [row]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(row);

  return (
    <div className="bg-white border-2 border-[#f4e4c6] rounded-3xl p-4 space-y-3" data-testid={`marketing-popup-row-${draft.id}`}>
      {!draft.enabled && (
        <div className="bg-[#fff4d6] border-2 border-[#f0a988] rounded-2xl px-3 py-2 text-xs text-[#a36b29] flex items-center gap-2" data-testid={`marketing-popup-off-warning-${draft.id}`}>
          <EyeOff className="w-4 h-4 shrink-0" />
          <span><b>Currently Off</b> — visitors won't see this. Click the Off pill below to flip it Live.</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onSave({ ...draft, enabled: !draft.enabled })}
            className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full"
            style={{ background: draft.enabled ? "#dcf2e1" : "#f1f5f8", color: draft.enabled ? "#236f6b" : "#6b7280" }}
            data-testid={`marketing-popup-toggle-${draft.id}`}
          >
            {draft.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {draft.enabled ? "Live" : "Off"}
          </button>
          <div className="flex flex-col">
            <button type="button" onClick={() => onMove(draft.id, -1)} className="text-[#5a6b76] hover:text-[#236f6b]"><ChevronUp className="w-3 h-3" /></button>
            <button type="button" onClick={() => onMove(draft.id, 1)} className="text-[#5a6b76] hover:text-[#236f6b]"><ChevronDown className="w-3 h-3" /></button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => onSave(draft)} disabled={!dirty} className="btn-primary inline-flex items-center gap-1 text-xs disabled:opacity-40" data-testid={`marketing-popup-save-${draft.id}`}>
            <Save className="w-3.5 h-3.5" /> Save
          </button>
          <button type="button" onClick={() => onDelete(draft.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-full" data-testid={`marketing-popup-delete-${draft.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[160px_1fr] gap-4">
        <ImageUploader
          label="Image (JPG/PNG)"
          value={draft.image_url || ""}
          onChange={(u) => set("image_url", u)}
          testid={`marketing-popup-image-${draft.id}`}
        />
        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76]">Title</span>
            <input value={draft.title || ""} onChange={(e) => set("title", e.target.value)} maxLength={120} className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" data-testid={`marketing-popup-title-${draft.id}`} />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76]">Body</span>
            <textarea value={draft.body || ""} onChange={(e) => set("body", e.target.value)} rows={3} maxLength={1200} className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" />
          </label>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76]">Button label</span>
          <input value={draft.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} maxLength={40} placeholder='e.g. "Buy the book"' className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Button URL</span>
          <input value={draft.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} placeholder="/shop or https://…" className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" />
        </label>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1"><Timer className="w-3 h-3" /> Delay (seconds)</span>
          <input type="number" min={0} max={600} value={draft.delay_seconds ?? 0} onChange={(e) => set("delay_seconds", Math.max(0, Math.min(600, Number(e.target.value) || 0)))} className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" data-testid={`marketing-popup-delay-${draft.id}`} />
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer mt-5">
          <input type="checkbox" checked={!!draft.show_signup_form} onChange={(e) => set("show_signup_form", e.target.checked)} className="w-4 h-4 accent-[#7fcfc7]" data-testid={`marketing-popup-signup-${draft.id}`} />
          <Mail className="w-4 h-4 text-[#5a8a6f]" /> Include newsletter signup form
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76]">Accent color</span>
          <input type="color" value={draft.accent_color || "#236f6b"} onChange={(e) => set("accent_color", e.target.value)} className="mt-1 w-full h-10 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3]" />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1"><Calendar className="w-3 h-3" /> Show from</span>
          <input type="datetime-local" value={isoToLocalInput(draft.starts_at)} onChange={(e) => set("starts_at", localInputToIso(e.target.value))} className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1"><Calendar className="w-3 h-3" /> Show until</span>
          <input type="datetime-local" value={isoToLocalInput(draft.ends_at)} onChange={(e) => set("ends_at", localInputToIso(e.target.value))} className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm" />
        </label>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                           */
/* ------------------------------------------------------------------ */

export default function AdminMarketing() {
  const [tab, setTab] = useState("banners");
  const [banners, setBanners] = useState([]);
  const [popups, setPopups] = useState([]);

  const load = async () => {
    try {
      const [b, p] = await Promise.all([
        api.get("/admin/marketing/banners"),
        api.get("/admin/marketing/popups"),
      ]);
      setBanners(b.data || []);
      setPopups(p.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load marketing");
    }
  };
  useEffect(() => { load(); }, []);

  /* ---- banners CRUD ---- */
  const createBanner = async () => {
    try {
      const { data } = await api.post("/admin/marketing/banners", {
        message: "New announcement",
        cta_label: "",
        cta_url: "",
        bg_color: "#236f6b",
        text_color: "#ffffff",
        enabled: true,
      });
      setBanners((b) => [...b, data]);
      toast.success("Banner created and Live");
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };
  const saveBanner = async (row) => {
    try {
      const { id, created_at, updated_at, ...patch } = row;
      const { data } = await api.patch(`/admin/marketing/banners/${id}`, patch);
      setBanners((b) => b.map((r) => (r.id === id ? data : r)));
      toast.success("Banner saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const deleteBanner = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await api.delete(`/admin/marketing/banners/${id}`);
      setBanners((b) => b.filter((r) => r.id !== id));
      toast.success("Banner deleted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };
  const moveBanner = async (id, dir) => {
    const sorted = [...banners].sort((a, b) => (a.position || 0) - (b.position || 0));
    const idx = sorted.findIndex((r) => r.id === id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= sorted.length) return;
    const a = sorted[idx], b = sorted[swap];
    try {
      await Promise.all([
        api.patch(`/admin/marketing/banners/${a.id}`, { position: b.position || (swap + 1) }),
        api.patch(`/admin/marketing/banners/${b.id}`, { position: a.position || (idx + 1) }),
      ]);
      load();
    } catch (err) { toast.error("Reorder failed"); }
  };

  /* ---- popups CRUD ---- */
  const createPopup = async () => {
    try {
      const { data } = await api.post("/admin/marketing/popups", {
        title: "New popup",
        body: "",
        image_url: "",
        cta_label: "",
        cta_url: "",
        delay_seconds: 5,
        show_signup_form: false,
        accent_color: "#236f6b",
        enabled: true,
      });
      setPopups((p) => [...p, data]);
      toast.success("Popup created and Live");
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };
  const savePopup = async (row) => {
    try {
      const { id, created_at, updated_at, ...patch } = row;
      const { data } = await api.patch(`/admin/marketing/popups/${id}`, patch);
      setPopups((p) => p.map((r) => (r.id === id ? data : r)));
      toast.success("Popup saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const deletePopup = async (id) => {
    if (!window.confirm("Delete this popup?")) return;
    try {
      await api.delete(`/admin/marketing/popups/${id}`);
      setPopups((p) => p.filter((r) => r.id !== id));
      toast.success("Popup deleted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };
  const movePopup = async (id, dir) => {
    const sorted = [...popups].sort((a, b) => (a.position || 0) - (b.position || 0));
    const idx = sorted.findIndex((r) => r.id === id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= sorted.length) return;
    const a = sorted[idx], b = sorted[swap];
    try {
      await Promise.all([
        api.patch(`/admin/marketing/popups/${a.id}`, { position: b.position || (swap + 1) }),
        api.patch(`/admin/marketing/popups/${b.id}`, { position: a.position || (idx + 1) }),
      ]);
      load();
    } catch (err) { toast.error("Reorder failed"); }
  };

  const sortedBanners = [...banners].sort((a, b) => (a.position || 0) - (b.position || 0));
  const sortedPopups = [...popups].sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <div className="space-y-5" data-testid="admin-marketing">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-accent text-3xl font-bold text-[#2e3a3a] flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-[#7fcfc7]" /> Marketing
          </h1>
          <p className="text-sm text-[#5a6b76] mt-1">
            Top scrolling banner across every page · home-page popups with images, links, and schedule windows.
          </p>
        </div>
      </header>

      <div className="flex gap-2 border-b border-[#f4e4c6]">
        <button onClick={() => setTab("banners")} className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${tab === "banners" ? "border-[#7fcfc7] text-[#236f6b]" : "border-transparent text-[#5a6b76]"}`} data-testid="marketing-tab-banners">
          Banners ({banners.length})
        </button>
        <button onClick={() => setTab("popups")} className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${tab === "popups" ? "border-[#7fcfc7] text-[#236f6b]" : "border-transparent text-[#5a6b76]"}`} data-testid="marketing-tab-popups">
          Popups ({popups.length})
        </button>
      </div>

      {tab === "banners" ? (
        <section className="space-y-3" data-testid="marketing-banners-pane">
          <div className="flex items-center justify-between">
            <div className="text-xs text-[#5a6b76]">
              Multiple banners scroll one after another across the top of every page. Toggle individual ones on/off, schedule by date, or pick brand colors.
            </div>
            <button onClick={createBanner} className="btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="marketing-banner-new">
              <Plus className="w-4 h-4" /> New banner
            </button>
          </div>
          {sortedBanners.length === 0 ? (
            <div className="bg-[#fffbf3] border-2 border-dashed border-[#f4e4c6] rounded-3xl p-10 text-center text-sm text-[#8a99a3]">
              No banners yet — click "New banner" to add one.
            </div>
          ) : (
            sortedBanners.map((r, idx) => (
              <BannerRow key={r.id} row={r} onSave={saveBanner} onDelete={deleteBanner} onMove={moveBanner} isFirst={idx === 0} />
            ))
          )}
        </section>
      ) : (
        <section className="space-y-3" data-testid="marketing-popups-pane">
          <div className="flex items-center justify-between">
            <div className="text-xs text-[#5a6b76]">
              Popups only show on the <b>home page</b>. After a visitor X's one out it won't reappear that visit; if they subscribe, no popup will appear again. Each popup has its own delay so you can chain (e.g. "Buy the book" at 0s, email signup at 30s).
            </div>
            <button onClick={createPopup} className="btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="marketing-popup-new">
              <Plus className="w-4 h-4" /> New popup
            </button>
          </div>
          {sortedPopups.length === 0 ? (
            <div className="bg-[#fffbf3] border-2 border-dashed border-[#f4e4c6] rounded-3xl p-10 text-center text-sm text-[#8a99a3]">
              No popups yet — click "New popup" to add one.
            </div>
          ) : (
            sortedPopups.map((r) => (
              <PopupRow key={r.id} row={r} onSave={savePopup} onDelete={deletePopup} onMove={movePopup} />
            ))
          )}
        </section>
      )}
    </div>
  );
}
